'use client'

import React, { ReactNode, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { FilterItem } from '@/ui/atoms/FilterItem'
import { sendGAEvent } from '@next/third-parties/google'
import { trackEvent } from '@/lib/analytics/posthog'

/**
 * Filter option type definition
 */
export type FilterOption = {
  label: string | ReactNode
  value: string
}

/**
 * Props for the FilterRow component
 */
export type FilterRowProps = {
  /** Array of filter options to display */
  filters: FilterOption[]
  /** Include 'All' filter option */
  includeAllFilter?: boolean
  /** Current active filter value */
  currentFilter: string
  /** Callback when filter changes */
  onFilterChange: (value: string) => void
  /** Title displayed next to the filter options */
  title?: string
  /** Additional CSS classes */
  className?: string
  /** Visual appearance */
  appearance?: 'default' | 'compact' | 'grid'
  /** Flag indicating if filters are being loaded */
  isLoading?: boolean
  /** Analytics context for PostHog enrichment */
  analyticsContext?: { filter_category?: string }
}

/**
 * FilterRow component displays a row of filter options that call the provided callback
 * when a filter is changed.
 */
export const FilterRow: React.FC<FilterRowProps> = ({
  title,
  filters,
  includeAllFilter = true,
  className,
  appearance = 'default',
  currentFilter = 'all',
  onFilterChange,
  isLoading = false,
  analyticsContext,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const activeItemRef = useRef<HTMLDivElement>(null)
  const [showLeftScroll, setShowLeftScroll] = useState(false)
  const [showRightScroll, setShowRightScroll] = useState(false)

  const updateScrollIndicators = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current
      setShowLeftScroll(container.scrollLeft > 0)
      setShowRightScroll(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 1
      )
    }
  }

  useEffect(() => {
    updateScrollIndicators()
    const container = scrollContainerRef.current
    if (container) {
      container.addEventListener('scroll', updateScrollIndicators)
      window.addEventListener('resize', updateScrollIndicators)
      return () => {
        container.removeEventListener('scroll', updateScrollIndicators)
        window.removeEventListener('resize', updateScrollIndicators)
      }
    }
  }, [])

  useEffect(() => {
    if (activeItemRef.current && scrollContainerRef.current) {
      const container = scrollContainerRef.current
      const activeItem = activeItemRef.current
      
      const containerRect = container.getBoundingClientRect()
      const activeItemRect = activeItem.getBoundingClientRect()
      
      const isNotFullyVisible = 
        activeItemRect.left < containerRect.left ||
        activeItemRect.right > containerRect.right

      if (isNotFullyVisible) {
        activeItem.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        })
      }
      
      // Update scroll indicators after scrolling
      setTimeout(updateScrollIndicators, 300)
    }
  }, [currentFilter])

  const handleFilterChange = (value: string) => {
    if (isLoading) return
    sendGAEvent('event', 'filterChange', {value: value})
    trackEvent('filterChange', { value, ...analyticsContext })
    onFilterChange(value)
  }

  // Grid appearance for many filter options
  if (appearance === 'grid') {
    return (
      <div className={classNames(className, 'w-full flex flex-col items-start')} data-pending={isLoading}>
        {title && (
          <span className="text-text-faded text-label-XS-semibold mb-3">
            {title}
          </span>
        )}
        <div className="w-full flex flex-wrap gap-2">
          {includeAllFilter && (
            <FilterItem
              onClick={() => handleFilterChange('all')}
              isActive={currentFilter === 'all'}
              disabled={isLoading}
            >
              All
            </FilterItem>
          )}
          {filters.map((filter) => (
            <FilterItem
              key={filter.value}
              onClick={() => handleFilterChange(filter.value)}
              isActive={currentFilter === filter.value}
              disabled={isLoading}
            >
              {filter.label}
            </FilterItem>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div
      className={classNames(className, 'max-w-full flex justify-between', {
        'flex-col items-start md:w-auto': appearance === 'compact',
        'flex-col md:flex-row items-start md:items-center': appearance === 'default',
        'w-full': title !== undefined,
      })}
      data-pending={isLoading}
    >
      <span
        className={`text-text-faded text-label-XS-semibold mb-2 ${appearance === 'default' && 'md:mb-0'}`}
      >
        {title}
      </span>
      <div className="relative max-w-full md:w-auto">
        {showLeftScroll && (
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-bg-base to-transparent z-10 pointer-events-none" />
        )}
        <div
          ref={scrollContainerRef}
          className={classNames(
            'max-w-full md:w-auto inline-flex space-x-1 flex-row justify-start items-center bg-bg-subtle p-1 rounded-sm overflow-x-auto scrollbar-hide',
            'scroll-smooth', // Add smooth scrolling behavior
          )}
          onScroll={updateScrollIndicators}
        >
          {includeAllFilter && (
            <React.Fragment key="all">
              <div ref={currentFilter === 'all' ? activeItemRef : null}>
                <FilterItem
                  onClick={() => handleFilterChange('all')}
                  isActive={currentFilter === 'all'}
                  disabled={isLoading}
                >
                  All
                </FilterItem>
              </div>
              {filters.length > 0 && <div className="h-4 w-[1px] bg-neutral-95" />}
            </React.Fragment>
          )}
          {filters.map((filter, index) => (
            <React.Fragment key={filter.value}>
              <div ref={currentFilter === filter.value ? activeItemRef : null}>
                <FilterItem
                  onClick={() => handleFilterChange(filter.value)}
                  isActive={currentFilter === filter.value}
                  disabled={isLoading}
                >
                  {filter.label}
                </FilterItem>
              </div>
              {index < filters.length - 1 && <div className="h-4 w-[1px] bg-neutral-95" />}
            </React.Fragment>
          ))}
        </div>
        {showRightScroll && (
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-bg-base to-transparent z-10 pointer-events-none" />
        )}
      </div>
    </div>
  )
}
